import { reactive } from 'vue'
import { defineStore } from 'pinia'
import apiPatientFavourites from 'src/components/common/FavouriteBtn/api/apiPatientFavourites'
import type { FavouriteType } from 'src/components/common/FavouriteBtn/types'

type FavouriteData = {
  timerId: ReturnType<typeof setTimeout> | null,
  promises: Array<Promise<null>>,
  ids: number[],
  favourites: Map<number, boolean>,
}

export default defineStore('favouriteBtnStore', () => {
  const byDoctorsData: FavouriteData = reactive({
    timerId: null,
    promises: [],
    ids: [],
    favourites: new Map(),
  })
  const byLpusData: FavouriteData = reactive({
    timerId: null,
    promises: [],
    ids: [],
    favourites: new Map(),
  })

  async function waitAllPromises(targetData: FavouriteData) {
    const resolvedPromises = await Promise.all(targetData.promises)

    if (resolvedPromises.length !== targetData.promises.length) {
      await waitAllPromises(targetData)
    }
  }

  async function getFavourites(id: number, type: FavouriteType) {
    const targetData = type === 'doctors'
      ? byDoctorsData
      : byLpusData

    if (!targetData.ids.includes(id)) {
      targetData.ids.push(id)
    }

    const promiseGetFavouriteData: Promise<null> = new Promise(resolve => {
      const timerId = setTimeout(async () => {
        if (timerId !== targetData.timerId) {
          resolve(null)
          return
        }

        try {
          const { data: favourites } = await apiPatientFavourites.getList(type, targetData.ids)

          // eslint-disable-next-line require-atomic-updates
          targetData.timerId = null
          // eslint-disable-next-line require-atomic-updates
          targetData.ids = []

          favourites.forEach((favourite => {
            targetData.favourites.set(favourite.id, favourite.in_user_favourites)
          }))
        } catch (e) { /* empty */ }

        resolve(null)
      }, 10)

      targetData.timerId = timerId
    })

    targetData.promises.push(promiseGetFavouriteData)
    await waitAllPromises(targetData)
    // eslint-disable-next-line require-atomic-updates
    targetData.promises = []

    return targetData.favourites
  }

  function setFavourite(
    { targetId, isFavourite, type }: { targetId: number, isFavourite: boolean, type: FavouriteType },
  ) {
    const targetData = type === 'doctors'
      ? byDoctorsData
      : byLpusData

    targetData.favourites.set(targetId, isFavourite)
  }

  return {
    getFavourites,
    setFavourite,
  }
})
